import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Plus, Target, CheckCircle, Circle, Calendar, X, Edit, Trash2 } from 'lucide-react';
import { mockGoals } from '../mock';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const GoalsPage = () => {
  const [children, setChildren] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    childId: '',
    title: '',
    description: '',
    targetDate: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [childrenData, goalsData] = await Promise.all([
        api.getChildren(),
        api.getGoals()
      ]);
      setChildren(childrenData);
      setGoals(goalsData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load goals',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async () => {
    try {
      const milestonesData = milestones.map((m, idx) => ({
        id: String(idx + 1),
        title: m,
        completed: false
      }));

      if (isEditMode) {
        // Update existing goal
        await api.updateGoal(editingGoalId, {
          title: formData.title,
          description: formData.description,
          target_date: new Date(formData.targetDate).toISOString(),
          milestones: milestonesData
        });
        
        toast({
          title: 'Goal Updated!',
          description: `${formData.title} has been updated successfully.`
        });
      } else {
        // Create new goal
        await api.createGoal({
          child_id: formData.childId,
          title: formData.title,
          description: formData.description,
          target_date: new Date(formData.targetDate).toISOString(),
          milestones: milestonesData
        });
        
        toast({
          title: 'Goal Created!',
          description: `${formData.title} has been added successfully.`
        });
      }

      await loadData();
      setFormData({ childId: '', title: '', description: '', targetDate: '' });
      setMilestones([]);
      setIsEditMode(false);
      setEditingGoalId(null);
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save goal',
        variant: 'destructive'
      });
    }
  };

  const handleEditGoal = (goal) => {
    setFormData({
      childId: goal.child_id,
      title: goal.title,
      description: goal.description,
      targetDate: goal.target_date ? goal.target_date.split('T')[0] : ''
    });
    setMilestones(goal.milestones ? goal.milestones.map(m => m.title) : []);
    setEditingGoalId(goal.id);
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDeleteGoal = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        await api.deleteGoal(goalId);
        toast({
          title: 'Goal Deleted',
          description: 'Goal has been removed successfully.'
        });
        await loadData();
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete goal',
          variant: 'destructive'
        });
      }
    }
  };

  const handleCloseDialog = (open) => {
    setIsDialogOpen(open);
    if (!open) {
      setIsEditMode(false);
      setEditingGoalId(null);
      setFormData({ childId: '', title: '', description: '', targetDate: '' });
      setMilestones([]);
    }
  };

  const toggleMilestone = async (goalId, milestoneId) => {
    try {
      const goal = goals.find(g => g.id === goalId);
      if (!goal) return;

      const updatedMilestones = goal.milestones.map(m =>
        m.id === milestoneId ? { ...m, completed: !m.completed } : m
      );
      const completedCount = updatedMilestones.filter(m => m.completed).length;
      const newProgress = updatedMilestones.length > 0
        ? Math.round((completedCount / updatedMilestones.length) * 100)
        : 0;

      await api.updateGoal(goalId, {
        milestones: updatedMilestones,
        progress: newProgress
      });

      await loadData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update milestone',
        variant: 'destructive'
      });
    }
  };

  const addMilestone = () => {
    setMilestones([...milestones, '']);
  };

  const removeMilestone = (index) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const updateMilestone = (index, value) => {
    const updated = [...milestones];
    updated[index] = value;
    setMilestones(updated);
  };

  const groupedGoals = children.map(child => ({
    child,
    goals: goals.filter(g => g.child_id === child.id)
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading goals...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Goals & Milestones</h1>
            <p className="text-slate-600 text-lg">Set targets and track progress</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white px-6 py-6">
                <Plus className="w-5 h-5 mr-2" />
                Create Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-900">
                  {isEditMode ? 'Edit Goal' : 'Create New Goal'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="child">Child</Label>
                  <Select value={formData.childId} onValueChange={(value) => setFormData({ ...formData, childId: value })}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select child" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map(child => (
                        <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="title">Goal Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Master Multiplication Tables"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the goal..."
                    rows={3}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="targetDate">Target Date</Label>
                  <Input
                    id="targetDate"
                    type="date"
                    value={formData.targetDate}
                    onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                    className="mt-2"
                  />
                </div>

                {/* Milestones/Achievements Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-base font-semibold">Milestones / Achievements</Label>
                      <p className="text-xs text-slate-500 mt-1">Break down the goal into smaller steps</p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addMilestone}
                      className="text-teal-600 hover:text-teal-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Milestone
                    </Button>
                  </div>

                  {milestones.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {milestones.map((milestone, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Circle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <Input
                            value={milestone}
                            onChange={(e) => updateMilestone(index, e.target.value)}
                            placeholder={`Milestone ${index + 1}`}
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMilestone(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                      <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No milestones added yet</p>
                      <p className="text-xs text-slate-400 mt-1">Click "Add Milestone" to break down this goal</p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleAddGoal}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6"
                  disabled={!formData.childId || !formData.title || !formData.targetDate}
                >
                  {isEditMode ? 'Update Goal' : 'Create Goal'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-8">
          {groupedGoals.map(({ child, goals: childGoals }) => (
            <div key={child.id}>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white"
                  style={{ backgroundColor: child.profileColor }}
                >
                  {child.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{child.name}</h2>
                  <p className="text-slate-600">{childGoals.length} active goals</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {childGoals.map(goal => (
                  <Card key={goal.id} className="p-6 border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-slate-900 mb-1">{goal.title}</h3>
                        <p className="text-sm text-slate-600">{goal.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditGoal(goal)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex-shrink-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteGoal(goal.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-slate-600">Progress</span>
                        <span className="font-semibold text-teal-700">{goal.progress}%</span>
                      </div>
                      <Progress value={goal.progress} className="h-3" />
                    </div>
                    {goal.milestones && goal.milestones.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-slate-700 mb-2">Milestones:</p>
                        <div className="space-y-2">
                          {goal.milestones.map(milestone => (
                            <div 
                              key={milestone.id} 
                              className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors"
                              onClick={() => toggleMilestone(goal.id, milestone.id)}
                            >
                              {milestone.completed ? (
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                              ) : (
                                <Circle className="w-5 h-5 text-slate-400 hover:text-teal-500 flex-shrink-0" />
                              )}
                              <span className={`text-sm ${
                                milestone.completed ? 'text-slate-500 line-through' : 'text-slate-700'
                              }`}>
                                {milestone.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-slate-500 pt-4 border-t border-slate-100">
                      <Calendar className="w-4 h-4" />
                      <span>Target: {new Date(goal.target_date).toLocaleDateString()}</span>
                    </div>
                  </Card>
                ))}

                {childGoals.length === 0 && (
                  <Card className="p-8 border-2 border-dashed border-slate-200 bg-slate-50 col-span-2">
                    <div className="text-center">
                      <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No goals set yet for {child.name}</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GoalsPage;